<template>
  <div class="main-content-container overflow-hidden">
    <PageTitle pageTitle="Payment Details & Verification" subTitle="Payments" />

    <div class="card bg-white border-0 rounded-3 mb-4" v-if="!loading && payment">
      <div class="card-body p-4">
        <div class="row">
          <div class="col-lg-4 col-sm-4 col-md-4">
            <div class="mb-4">
              <p class="mb-4">
                Payment ID: <span class="text-secondary">#{{ payment.payment_id }}</span>
              </p>
              <p class="mb-1">Customer:</p>
              <p class="mb-1 fw-medium text-dark">{{ payment.order?.customer?.name || '-' }}</p>
              <p class="mb-1 text-secondary">
                {{ payment.order?.customer?.phone_number || '-' }}
              </p>
              <p class="mb-1 text-secondary">
                Status: 
                <span class="badge" :class="statusClass(payment.payment_status)">
                  {{ payment.payment_status }}
                </span>
              </p>
            </div>
          </div>
          <div class="col-lg-4 col-sm-4 col-md-4">
            <div class="mb-4 text-center">
              <h4 class="fs-16 fw-medium mb-3">Order Info</h4>
              <p class="mb-1 text-secondary">Code: <RouterLink :to="`/orders/${payment.order_id}`">{{ payment.order?.order_code }}</RouterLink></p>
              <p class="mb-1 text-secondary">Service: {{ payment.order?.service_type }}</p>
            </div>
          </div>
          <div class="col-lg-4 col-sm-4 col-md-4">
            <div class="mb-4 text-sm-end">
              <p class="mb-4">
                Date: <span class="text-secondary">{{ new Date(payment.created_at).toLocaleDateString() }}</span>
              </p>
              <p class="mb-1">Pay To:</p>
              <p class="mb-1 fw-medium text-dark">{{ payment.channel?.provider_name }}</p>
              <p class="mb-1 text-secondary">{{ payment.channel?.account_number }}</p>
              <p class="mb-1 text-secondary">{{ payment.channel?.account_name }}</p>
            </div>
          </div>
        </div>

        <div class="default-table-area all-products">
          <div class="table-responsive">
            <table class="table align-middle">
              <thead>
                <tr>
                  <th scope="col">No</th>
                  <th scope="col">Item Description</th>
                  <th scope="col">Quantity</th>
                  <th scope="col">Total</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td class="text-body">1</td>
                  <td class="text-secondary fw-medium">
                    {{ payment.order?.service?.service_name || 'Service Order' }}
                  </td>
                  <td class="text-body">1</td>
                  <td class="text-body">Rp {{ Number(payment.order?.total_price).toLocaleString('id-ID') }}</td>
                </tr>
                <tr>
                  <td></td>
                  <td></td>
                  <td class="fw-medium text-secondary">Grand Total</td>
                  <td class="text-secondary fw-bold fs-16">Rp {{ Number(payment.order?.total_price).toLocaleString('id-ID') }}</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- Payment Proof Section -->
        <div class="mt-5 border-top pt-4">
          <h4 class="mb-3">Payment Proof</h4>
          <div v-if="payment.proof" class="row">
            <div class="col-lg-6">
              <ul class="list-group list-group-flush mb-4">
                <li class="list-group-item px-0 pb-2 pt-0 d-flex justify-content-between">
                  <span class="text-secondary">Sender Name</span>
                  <span class="fw-medium text-dark">{{ payment.proof.sender_name || '-' }}</span>
                </li>
                <li class="list-group-item px-0 py-2 d-flex justify-content-between">
                  <span class="text-secondary">Sender Bank</span>
                  <span class="fw-medium text-dark">{{ payment.proof.sender_bank || '-' }}</span>
                </li>
                <li class="list-group-item px-0 py-2 d-flex justify-content-between">
                  <span class="text-secondary">Verification Status</span>
                  <span class="badge" :class="proofStatusClass(payment.proof.verification_status)">
                    {{ payment.proof.verification_status }}
                  </span>
                </li>
                <li class="list-group-item px-0 py-2 d-flex justify-content-between" v-if="payment.proof.verified_by">
                  <span class="text-secondary">Verified By</span>
                  <span class="fw-medium text-dark">
                    {{ payment.proof.verified_by.name }}
                    <small class="text-muted">({{ payment.proof.verified_by.email }})</small>
                  </span>
                </li>
                <li class="list-group-item px-0 py-2 d-flex justify-content-between" v-if="payment.proof.rejection_reason">
                  <span class="text-secondary">Rejection Reason</span>
                  <span class="text-danger">{{ payment.proof.rejection_reason }}</span>
                </li>
              </ul>
              
              <!-- Action Buttons -->
              <div class="d-flex flex-column gap-3 mt-4" v-if="payment.payment_status !== 'Paid' && payment.proof?.verification_status !== 'Verified'">
                <h6 class="mb-1">Verification Action</h6>
                <div class="mb-2">
                  <label class="form-label">Rejection Reason (Optional, only if rejecting)</label>
                  <textarea class="form-control" v-model="rejectionReason" rows="2"></textarea>
                </div>
                <div class="d-flex gap-2">
                  <button class="btn btn-danger py-2 px-4 fw-medium flex-grow-1 text-white" @click="verifyPayment('Rejected')" :disabled="!payment.proof">
                    <i class="ri-close-circle-line me-1"></i> Reject
                  </button>
                  <button class="btn btn-success py-2 px-4 fw-medium flex-grow-1 text-white" @click="verifyPayment('Verified')" :disabled="!payment.proof">
                    <i class="ri-check-line me-1"></i> Confirm Payment
                  </button>
                </div>
              </div>
            </div>
            
            <div class="col-lg-6 text-center">
              <a :href="getFileUrl(payment.proof.proof_file)" target="_blank" class="d-block bg-light p-3 rounded h-100 d-flex align-items-center justify-content-center">
                <img :src="getFileUrl(payment.proof.proof_file)" class="img-fluid rounded shadow-sm" style="max-height: 400px; object-fit: contain;" alt="Payment Proof" v-if="isImage(payment.proof.proof_file)" />
                <div v-else class="text-center">
                  <i class="ri-file-text-line text-secondary mb-2 d-block" style="font-size: 4rem;"></i>
                  <span class="fs-16 fw-medium">View Document Proof</span>
                </div>
              </a>
            </div>
          </div>
          <div v-else class="alert alert-secondary text-center mt-3 p-4">
            Customer has not uploaded any payment proof yet.
          </div>
        </div>
      </div>
    </div>
    
    <div v-else-if="loading" class="text-center p-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">Loading...</span>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, onMounted } from "vue";
import { useRoute } from "vue-router";
import PageTitle from "@/components/Common/PageTitle.vue";
import paymentService from "@/services/paymentService";

export default defineComponent({
  name: "PaymentDetailPage",
  components: {
    PageTitle,
  },
  setup() {
    const route = useRoute();
    const payment = ref<any>(null);
    const loading = ref(true);
    const rejectionReason = ref('');

    const fetchPayment = async (id: number) => {
      loading.value = true;
      try {
        const response = await paymentService.getById(id);
        payment.value = response.data;
      } catch (error) {
        console.error("Error fetching payment:", error);
      } finally {
        loading.value = false;
      }
    };

    const verifyPayment = async (status: 'Verified' | 'Rejected') => {
      if (!payment.value || !payment.value.proof) return;
      
      if (status === 'Rejected' && !rejectionReason.value && !confirm('Are you sure you want to reject without a reason?')) {
        return;
      }

      try {
        await paymentService.verify(payment.value.payment_id, {
          status: status,
          rejection_reason: status === 'Verified' ? null : rejectionReason.value
        });
        alert(`Payment has been ${status.toLowerCase()} successfully.`);
        // Refresh data
        await fetchPayment(payment.value.payment_id);
      } catch (error) {
        console.error("Error verifying payment:", error);
        alert("Failed to verify payment.");
      }
    };

    const statusClass = (status: string) => {
      switch (status) {
        case 'Paid': return 'bg-success bg-opacity-10 text-success';
        case 'Unpaid': return 'bg-secondary bg-opacity-10 text-secondary';
        case 'Awaiting Verification': return 'bg-warning bg-opacity-10 text-warning';
        case 'Failed': return 'bg-danger bg-opacity-10 text-danger';
        default: return 'bg-primary bg-opacity-10 text-primary';
      }
    };

    const proofStatusClass = (status: string) => {
      switch (status) {
        case 'Verified': return 'bg-success text-white';
        case 'Rejected': return 'bg-danger text-white';
        case 'Pending': return 'bg-warning text-dark';
        default: return 'bg-secondary text-white';
      }
    };

    const getFileUrl = (path: string) => {
      if (!path) return '';
      if (path.startsWith('http')) return path;
      const baseUrl = process.env.NODE_ENV === 'production'
        ? 'https://backend-beta.rayacheck.id'
        : 'http://jurnal-backend.test';
      return `${baseUrl}/storage/${path}`;
    };

    const isImage = (path: string) => {
      if (!path) return false;
      const lower = path.toLowerCase();
      return lower.endsWith('.jpg') || lower.endsWith('.jpeg') || lower.endsWith('.png') || lower.endsWith('.webp');
    };

    onMounted(() => {
      if (route.params.id) {
        fetchPayment(Number(route.params.id));
      }
    });

    return {
      payment,
      loading,
      rejectionReason,
      verifyPayment,
      statusClass,
      proofStatusClass,
      getFileUrl,
      isImage
    };
  },
});
</script>
